// @upryzze/forms — safe-expression engine: tokenizer (source → tokens).
// Hand-written so the accepted character set is explicit and auditable.

import { EXPR_LIMITS, EXPR_ERROR } from "./constants.js";

/** A thrown parse-time error carrying a stable `code` + character position. */
export class ExprSyntaxError extends Error {
  constructor(code, message, pos) {
    super(message);
    this.name = "ExprSyntaxError";
    this.code = code;
    this.pos = pos;
  }
}

// Token types: 'num' | 'str' | 'ident' | 'bool' | 'null' | 'op' | 'punc' | 'eof'
const MULTI_OPS = ["||", "&&", "==", "!=", "<=", ">="];
const SINGLE_OPS = new Set(["+", "-", "*", "/", "%", "<", ">", "!"]);
const PUNCT = new Set(["(", ")", ",", "?", ":"]);

const isDigit = (c) => c >= "0" && c <= "9";
const isIdentStart = (c) =>
  (c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || c === "_";
const isIdentPart = (c) => isIdentStart(c) || isDigit(c);

export function tokenize(source) {
  if (typeof source !== "string") {
    throw new ExprSyntaxError(EXPR_ERROR.SYNTAX, "expression must be a string", 0);
  }
  if (source.length > EXPR_LIMITS.MAX_SOURCE_LENGTH) {
    throw new ExprSyntaxError(
      EXPR_ERROR.TOO_LONG,
      `expression exceeds ${EXPR_LIMITS.MAX_SOURCE_LENGTH} characters`,
      0,
    );
  }

  const tokens = [];
  let i = 0;
  const n = source.length;

  while (i < n) {
    const c = source[i];

    // whitespace
    if (c === " " || c === "\t" || c === "\n" || c === "\r") {
      i += 1;
      continue;
    }

    // string literal: '...' or "..." with \' \" \\ escapes, no newlines
    if (c === "'" || c === '"') {
      const quote = c;
      let j = i + 1;
      let value = "";
      while (j < n && source[j] !== quote) {
        const ch = source[j];
        if (ch === "\n" || ch === "\r") {
          throw new ExprSyntaxError(
            EXPR_ERROR.SYNTAX,
            "newline in string literal",
            j,
          );
        }
        if (ch === "\\") {
          const next = source[j + 1];
          if (next === quote || next === "\\") {
            value += next;
            j += 2;
            continue;
          }
          throw new ExprSyntaxError(
            EXPR_ERROR.SYNTAX,
            "invalid escape in string literal",
            j,
          );
        }
        value += ch;
        j += 1;
      }
      if (j >= n) {
        throw new ExprSyntaxError(
          EXPR_ERROR.SYNTAX,
          "unterminated string literal",
          i,
        );
      }
      tokens.push({ type: "str", value, pos: i });
      i = j + 1;
      continue;
    }

    // number literal: 123 | 1.5 | .5  (no exponent, no sign — unary minus handles sign)
    if (isDigit(c) || (c === "." && isDigit(source[i + 1]))) {
      let j = i;
      let seenDot = false;
      while (j < n && (isDigit(source[j]) || source[j] === ".")) {
        if (source[j] === ".") {
          if (seenDot) {
            throw new ExprSyntaxError(
              EXPR_ERROR.SYNTAX,
              "malformed number (multiple decimal points)",
              j,
            );
          }
          seenDot = true;
        }
        j += 1;
      }
      const raw = source.slice(i, j);
      // Guard against a trailing identifier char glued to a number (e.g. 1abc)
      if (j < n && isIdentStart(source[j])) {
        throw new ExprSyntaxError(
          EXPR_ERROR.SYNTAX,
          "invalid number literal",
          i,
        );
      }
      tokens.push({ type: "num", value: Number(raw), pos: i });
      i = j;
      continue;
    }

    // identifier / keyword
    if (isIdentStart(c)) {
      let j = i + 1;
      while (j < n && isIdentPart(source[j])) j += 1;
      const name = source.slice(i, j);
      if (name === "true" || name === "false") {
        tokens.push({ type: "bool", value: name === "true", pos: i });
      } else if (name === "null") {
        tokens.push({ type: "null", value: null, pos: i });
      } else {
        tokens.push({ type: "ident", value: name, pos: i });
      }
      i = j;
      continue;
    }

    // multi-char operators
    const two = source.slice(i, i + 2);
    if (MULTI_OPS.includes(two)) {
      tokens.push({ type: "op", value: two, pos: i });
      i += 2;
      continue;
    }

    // single-char operators
    if (SINGLE_OPS.has(c)) {
      tokens.push({ type: "op", value: c, pos: i });
      i += 1;
      continue;
    }

    // punctuation
    if (PUNCT.has(c)) {
      tokens.push({ type: "punc", value: c, pos: i });
      i += 1;
      continue;
    }

    // Anything else (=, &, |, ., [, ], {, }, ;, @, backtick, $, etc.) is
    // rejected outright — this is what keeps assignment, member access,
    // indexing, template strings, and statements out of the language.
    throw new ExprSyntaxError(
      EXPR_ERROR.SYNTAX,
      `unexpected character ${JSON.stringify(c)}`,
      i,
    );
  }

  tokens.push({ type: "eof", value: null, pos: n });
  return tokens;
}
